import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack } from "@mui/material";
import ArticleParagraphToolbar from "./ArticleParagraphToolbar";
import {
  contentArticleParagraphEditorShellSx,
  contentArticleParagraphEditorSx,
} from "../contentEditorStyles";
import {
  ARTICLE_DEFAULT_FONT_SIZE,
} from "../../../../utils/articleEditorColorPalette";
import {
  DEFAULT_PARAGRAPH_ALIGN,
  canonicalizeParagraphEditorDom,
  compactLineAligns,
  getRangeCharacterOffset,
  getSelectedLineIndices,
  insertLineBreakAtSelection,
  normalizeParagraphBlock,
  resolveParagraphAlign,
  runsToEditorHtml,
  setSelectionCharacterRange,
  splitRunsIntoLines,
  usesLineAlignLayout,
} from "../../../../utils/articleParagraphUtils";
import {
  applyColorToSelection,
  applyFontSizeToSelection,
  execEditorCommand,
  hasEditorFormatContext,
  hasEditorTextSelection,
  insertTabAtSelection,
  insertTextAtSelection,
  readSelectionToolbarState,
  saveEditorSelection,
} from "./articleParagraphEditorCommands";

const DEFAULT_TEXT_COLOR = "#1f2937";

const buildBlockSignature = (runs, align, lineAligns) =>
  JSON.stringify({
    runs,
    align: align || DEFAULT_PARAGRAPH_ALIGN,
    lineAligns: lineAligns || [],
  });

const RichParagraphEditor = ({ block, onChange }) => {
  const editorRef = useRef(null);
  const colorAnchorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const skipNextInputRef = useRef(false);
  const lastSignatureRef = useRef("");
  const [colorAnchor, setColorAnchor] = useState(null);
  const [pickerColor, setPickerColor] = useState(DEFAULT_TEXT_COLOR);
  const [fontSizeChoice, setFontSizeChoice] = useState(ARTICLE_DEFAULT_FONT_SIZE);
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    hasTextSelection: false,
    formatActive: false,
    align: DEFAULT_PARAGRAPH_ALIGN,
  });

  const normalizedBlock = normalizeParagraphBlock(block);
  const runsSignature = buildBlockSignature(
    normalizedBlock.runs,
    normalizedBlock.align,
    normalizedBlock.lineAligns
  );
  const paragraphUsesLineLayout = usesLineAlignLayout(normalizedBlock);

  useEffect(() => {
    document.execCommand("styleWithCSS", false, true);
  }, []);

  const emitBlock = useCallback(
    (patch) => {
      const next = {
        type: "paragraph",
        runs: normalizedBlock.runs,
        ...(normalizedBlock.align ? { align: normalizedBlock.align } : {}),
        ...(normalizedBlock.lineAligns ? { lineAligns: normalizedBlock.lineAligns } : {}),
        ...patch,
      };
      if (Object.prototype.hasOwnProperty.call(patch, "lineAligns") && patch.lineAligns == null) {
        delete next.lineAligns;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "align") && patch.align == null) {
        delete next.align;
      }
      onChange(next);
    },
    [normalizedBlock.align, normalizedBlock.lineAligns, normalizedBlock.runs, onChange]
  );

  const refreshToolbarState = useCallback(() => {
    const toolbarState = readSelectionToolbarState(editorRef.current, normalizedBlock);
    setFormatState({
      bold: toolbarState.bold,
      italic: toolbarState.italic,
      hasTextSelection: toolbarState.hasTextSelection,
      formatActive: toolbarState.formatActive,
      align: toolbarState.hasTextSelection
        ? toolbarState.align
        : resolveParagraphAlign(normalizedBlock),
    });
    if (toolbarState.formatActive) {
      setFontSizeChoice(toolbarState.fontSize || ARTICLE_DEFAULT_FONT_SIZE);
      setPickerColor(toolbarState.color || DEFAULT_TEXT_COLOR);
    }
  }, [normalizedBlock]);

  const renderEditorContent = useCallback(
    (runs, lineAligns, paragraphAlign = normalizedBlock.align) => {
      if (!editorRef.current) {
        return;
      }
      skipNextInputRef.current = true;
      editorRef.current.innerHTML = runsToEditorHtml(runs, {
        paragraphAlign,
        lineAligns,
      });
    },
    [normalizedBlock.align]
  );

  const syncFromDom = useCallback(() => {
    if (!editorRef.current) {
      return;
    }

    const htmlBefore = editorRef.current.innerHTML;
    const { runs, lineAligns } = canonicalizeParagraphEditorDom(editorRef.current, {
      paragraphAlign: normalizedBlock.align,
    });
    if (htmlBefore !== editorRef.current.innerHTML) {
      skipNextInputRef.current = true;
    }
    lastSignatureRef.current = buildBlockSignature(
      runs,
      normalizedBlock.align,
      lineAligns
    );
    emitBlock({ runs, lineAligns });
    refreshToolbarState();
  }, [emitBlock, normalizedBlock.align, refreshToolbarState]);

  const captureEditorSelectionOffsets = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) {
      return null;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return null;
    }
    const start = getRangeCharacterOffset(editor, range.startContainer, range.startOffset);
    const end = getRangeCharacterOffset(editor, range.endContainer, range.endOffset);
    if (typeof start !== "number" || typeof end !== "number") {
      return null;
    }
    return { start, end };
  }, []);

  const restoreEditorSelectionOffsets = useCallback((offsets) => {
    if (!offsets || !editorRef.current) {
      return;
    }
    setSelectionCharacterRange(editorRef.current, offsets.start, offsets.end);
  }, []);

  const syncFromDomPreservingSelection = useCallback(
    (offsetsBefore = null) => {
      const offsets = offsetsBefore || captureEditorSelectionOffsets();
      syncFromDom();
      if (offsets) {
        restoreEditorSelectionOffsets(offsets);
        refreshToolbarState();
      }
    },
    [captureEditorSelectionOffsets, refreshToolbarState, restoreEditorSelectionOffsets, syncFromDom]
  );

  const handleEditorInput = () => {
    if (skipNextInputRef.current) {
      skipNextInputRef.current = false;
      return;
    }
    syncFromDom();
  };

  useEffect(() => {
    if (!editorRef.current || runsSignature === lastSignatureRef.current) {
      return;
    }
    if (document.activeElement === editorRef.current) {
      return;
    }
    lastSignatureRef.current = runsSignature;
    renderEditorContent(normalizedBlock.runs, normalizedBlock.lineAligns);
  }, [
    normalizedBlock.align,
    normalizedBlock.lineAligns,
    normalizedBlock.runs,
    renderEditorContent,
    runsSignature,
  ]);

  const handleFontSizeChange = (figmaPx) => {
    if (!hasEditorFormatContext(editorRef.current)) {
      return;
    }
    const offsets = captureEditorSelectionOffsets();
    setFontSizeChoice(figmaPx);
    if (applyFontSizeToSelection(editorRef.current, figmaPx)) {
      syncFromDomPreservingSelection(offsets);
    }
  };

  const handleColorDraftChange = (color) => {
    setPickerColor(color);
  };

  const handleColorApply = (color) => {
    const savedRange = savedSelectionRef.current;
    if (!savedRange && !hasEditorFormatContext(editorRef.current)) {
      return;
    }
    const offsets = savedRange
      ? {
          start: getRangeCharacterOffset(
            editorRef.current,
            savedRange.startContainer,
            savedRange.startOffset
          ),
          end: getRangeCharacterOffset(
            editorRef.current,
            savedRange.endContainer,
            savedRange.endOffset
          ),
        }
      : captureEditorSelectionOffsets();
    setPickerColor(color);
    if (applyColorToSelection(editorRef.current, color, savedRange)) {
      savedSelectionRef.current = null;
      if (offsets && typeof offsets.start === "number" && typeof offsets.end === "number") {
        syncFromDomPreservingSelection(offsets);
      } else {
        syncFromDom();
      }
    }
  };

  const handleColorPopoverClose = () => {
    setColorAnchor(null);
    savedSelectionRef.current = null;
  };

  const execFormat = (command) => {
    if (!hasEditorFormatContext(editorRef.current)) {
      return;
    }
    const offsets = captureEditorSelectionOffsets();
    execEditorCommand(editorRef.current, command);
    syncFromDomPreservingSelection(offsets);
  };

  const handleEditorKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      insertTabAtSelection(editorRef.current);
      syncFromDom();
      return;
    }

    if (event.key === " " || event.code === "Space") {
      event.preventDefault();
      event.stopPropagation();
      insertTextAtSelection(editorRef.current, " ");
      syncFromDom();
      return;
    }

    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (insertLineBreakAtSelection(editorRef.current, { paragraphAlign: normalizedBlock.align })) {
      syncFromDom();
    }
  };

  const handleEditorPaste = (event) => {
    event.preventDefault();
    const plainText = event.clipboardData?.getData("text/plain") ?? "";
    if (!plainText || !editorRef.current) {
      return;
    }
    editorRef.current.focus();
    const lines = plainText.replace(/\r\n/g, "\n").split("\n");
    lines.forEach((line, index) => {
      if (index > 0) {
        insertLineBreakAtSelection(editorRef.current, { paragraphAlign: normalizedBlock.align });
      }
      insertTextAtSelection(editorRef.current, line);
    });
    syncFromDom();
  };

  const handleAlignChange = (_, nextAlign) => {
    if (!nextAlign) {
      return;
    }

    if (formatState.hasTextSelection && hasEditorTextSelection(editorRef.current)) {
      const { runs, lineAligns: domLineAligns } = canonicalizeParagraphEditorDom(editorRef.current, {
        paragraphAlign: normalizedBlock.align,
      });
      const lineCount = splitRunsIntoLines(runs).length;
      const defaultAlign = resolveParagraphAlign(normalizedBlock);
      const mergedLineAligns = Array.from({ length: lineCount }, (_, index) =>
        domLineAligns?.[index] ?? normalizedBlock.lineAligns?.[index]
      );
      getSelectedLineIndices(editorRef.current).forEach((lineIndex) => {
        mergedLineAligns[lineIndex] = nextAlign === defaultAlign ? undefined : nextAlign;
      });
      const lineAligns = compactLineAligns(mergedLineAligns, lineCount, defaultAlign);
      lastSignatureRef.current = buildBlockSignature(
        runs,
        normalizedBlock.align,
        lineAligns
      );
      renderEditorContent(runs, lineAligns || mergedLineAligns);
      emitBlock({ runs, lineAligns });
      refreshToolbarState();
      return;
    }

    const nextParagraphAlign = nextAlign === DEFAULT_PARAGRAPH_ALIGN ? undefined : nextAlign;
    lastSignatureRef.current = buildBlockSignature(
      normalizedBlock.runs,
      nextParagraphAlign,
      undefined
    );
    emitBlock({
      align: nextParagraphAlign,
      lineAligns: undefined,
    });
    renderEditorContent(normalizedBlock.runs, undefined, nextParagraphAlign);
  };

  return (
    <Stack spacing={0} sx={contentArticleParagraphEditorShellSx}>
      <ArticleParagraphToolbar
        align={
          formatState.hasTextSelection
            ? formatState.align || false
            : normalizedBlock.align || DEFAULT_PARAGRAPH_ALIGN
        }
        boldActive={formatState.bold}
        italicActive={formatState.italic}
        formatActive={formatState.formatActive}
        selectionActive={formatState.hasTextSelection}
        fontSizeValue={fontSizeChoice}
        colorValue={pickerColor}
        colorAnchorRef={colorAnchorRef}
        colorAnchor={colorAnchor}
        onBoldToggle={() => execFormat("bold")}
        onItalicToggle={() => execFormat("italic")}
        onFontSizeChange={handleFontSizeChange}
        onColorDraftChange={handleColorDraftChange}
        onColorApply={handleColorApply}
        onColorAnchorOpen={() => {
          if (!hasEditorFormatContext(editorRef.current)) {
            return;
          }
          savedSelectionRef.current = saveEditorSelection(editorRef.current);
          setColorAnchor(colorAnchorRef.current);
        }}
        onColorAnchorClose={handleColorPopoverClose}
        onAlignChange={handleAlignChange}
      />

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleEditorInput}
        onFocus={refreshToolbarState}
        onBlur={syncFromDom}
        onKeyUp={refreshToolbarState}
        onMouseUp={refreshToolbarState}
        onKeyDown={handleEditorKeyDown}
        onPaste={handleEditorPaste}
        sx={{
          ...contentArticleParagraphEditorSx,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          textAlign: paragraphUsesLineLayout ? undefined : (normalizedBlock.align || DEFAULT_PARAGRAPH_ALIGN),
        }}
        aria-label="Editor paragrafo"
      />
    </Stack>
  );
};

RichParagraphEditor.propTypes = {
  block: PropTypes.shape({
    type: PropTypes.string,
    align: PropTypes.oneOf(["left", "center", "justify"]),
    lineAligns: PropTypes.arrayOf(PropTypes.oneOf(["left", "center", "justify"])),
    runs: PropTypes.arrayOf(PropTypes.object),
    text: PropTypes.string,
    marks: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default RichParagraphEditor;
